"use client";

import React from "react";
import Link from "next/link";
import clsx from "clsx";

interface CareerCardProps {
  title: string;
  location: string;
  /** Optional posted date shown beneath the location. */
  postedDate?: string;
  href?: string;
  ctaLabel?: string;
  /** Figma "Selected" state — persistent white/10 overlay on the gradient. */
  selected?: boolean;
  onClick?: () => void;
  className?: string;
}

/**
 * Career listing row matching the Figma "Career card" component.
 * Layout: left arrow icon, job title + location (and optional posted date),
 * right-aligned "Apply" CTA. Hover/selected states lighten the gradient
 * background, matching the Figma states.
 */
const CareerCard: React.FC<CareerCardProps> = ({
  title,
  location,
  postedDate,
  href,
  ctaLabel = "Apply",
  selected = false,
  onClick,
  className,
}) => {
  const Inner = (
    <div className="relative flex items-center gap-6 md:gap-8 px-6 md:px-8 py-6 md:py-8">
      <svg
        width="15"
        height="14"
        viewBox="0 0 15.043 14"
        fill="none"
        className="flex-shrink-0 rotate-180 text-primary"
      >
        <path
          d="M5.46846 0.390936L0.351547 6.05401C-0.117182 6.5759 -0.117182 7.423 0.351547 7.94314L5.46846 13.6096C5.94112 14.1315 6.70615 14.1288 7.17879 13.6096C7.64673 13.0868 7.64673 12.2432 7.17879 11.7204L4.1853 8.40809H13.788C14.4817 8.40809 15.043 7.78303 15.043 7.00938C15.043 6.2366 14.4817 5.61068 13.788 5.61068H4.16724L7.17879 2.28087C7.64673 1.75898 7.64673 0.914499 7.17879 0.392615C6.70691 -0.130143 5.94188 -0.131016 5.46846 0.390866V0.390936Z"
          fill="currentColor"
        />
      </svg>

      <div className="flex-1 flex flex-col gap-1 min-w-0 text-left">
        <p className="text-white text-xl font-semibold tracking-[0.15px] truncate">{title}</p>
        <p className="text-text-default text-base leading-7 tracking-[0.5px]">{location}</p>
        {postedDate && (
          <p className="text-text-default/70 text-sm leading-5 tracking-[0.25px]">{postedDate}</p>
        )}
      </div>

      <span className="text-primary text-base font-semibold tracking-[0.5px] leading-6 whitespace-nowrap">
        {ctaLabel}
      </span>
    </div>
  );

  const wrapperClass = clsx(
    "group/career relative block w-full overflow-hidden rounded-lg bg-gradient-to-r from-surface-raised to-background transition-colors duration-300 ease-out",
    // Figma "Hovered": surface lightens (white/5 overlay) — no lift, no border.
    "hover:bg-[linear-gradient(90deg,rgba(255,255,255,0.05)_0%,rgba(255,255,255,0.05)_100%),linear-gradient(90deg,#1f2428_0%,#15181d_100%)]",
    // Figma "Selected": persistent white/10 overlay.
    selected &&
      "bg-[linear-gradient(90deg,rgba(255,255,255,0.1)_0%,rgba(255,255,255,0.1)_100%),linear-gradient(90deg,#1f2428_0%,#15181d_100%)]",
    className
  );

  if (href) {
    return (
      <Link href={href} className={wrapperClass} onClick={onClick}>
        {Inner}
      </Link>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(wrapperClass, "cursor-pointer text-left")}
    >
      {Inner}
    </button>
  );
};

export default CareerCard;
export { CareerCard };
